/**
 * Wishlist — local-first sync.
 *
 * Behaviour:
 *   - Every heart click updates localStorage + UI immediately.
 *   - For logged-in users, a debounced /sync POST flushes the full list to the
 *     Cloudflare Worker (App Proxy at WishlistConfig.proxyBase). Bursts of
 *     clicks within DEBOUNCE_MS collapse into a single request.
 *   - On page hide/unload, any pending changes flush via sendBeacon so the
 *     last edit isn't lost when the user navigates away.
 *   - If a flush fails, a "pending" flag is set in localStorage; the next page
 *     load reads it and retries.
 *   - On first load after login, the union of localStorage and the server's
 *     initialIds is pushed to /sync so anonymous saves get adopted by the
 *     account.
 *
 * Storage keys:
 *   wishlist          JSON array of numeric product ID strings (client source of truth)
 *   wishlist:pending  "1" if the latest list hasn't been confirmed by the Worker yet
 *
 * window.WishlistConfig (from snippets/wishlist-init.liquid):
 *   { loggedIn: bool, proxyBase: string, initialIds: number[] }
 *
 * Buttons: any [data-wishlist-btn][data-product-id="<id>"] in the page.
 * Event:   document dispatches "wishlist:change" with detail { productId, saved }.
 */

(function () {
  var STORAGE_KEY = 'wishlist';
  var PENDING_KEY = 'wishlist:pending';
  var LEGACY_KEY = 'bookmarks';
  var LEGACY_PENDING_KEY = 'bookmarks:pending';
  var DEBOUNCE_MS = 1500;

  var config = window.WishlistConfig || { loggedIn: false, proxyBase: '/apps/wishlist', initialIds: [] };
  var saved = new Set();
  var flushTimer = null;

  // --- one-time migration from the old "bookmarks" key ------------------

  try {
    if (localStorage.getItem(STORAGE_KEY) === null && localStorage.getItem(LEGACY_KEY) !== null) {
      localStorage.setItem(STORAGE_KEY, localStorage.getItem(LEGACY_KEY));
    }
    localStorage.removeItem(LEGACY_KEY);
    if (localStorage.getItem(LEGACY_PENDING_KEY) === '1' && localStorage.getItem(PENDING_KEY) === null) {
      localStorage.setItem(PENDING_KEY, '1');
    }
    localStorage.removeItem(LEGACY_PENDING_KEY);
  } catch (e) {}

  // --- storage ----------------------------------------------------------

  function readLocal() {
    try {
      var raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return raw.filter(function (v) { return /^\d+$/.test(String(v)); }).map(String);
    } catch (e) { return []; }
  }

  function writeLocal() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(saved)));
    } catch (e) {}
  }

  function setPending(v) {
    try {
      if (v) localStorage.setItem(PENDING_KEY, '1');
      else localStorage.removeItem(PENDING_KEY);
    } catch (e) {}
  }

  function hasPending() {
    try { return localStorage.getItem(PENDING_KEY) === '1'; } catch (e) { return false; }
  }

  // --- network ----------------------------------------------------------

  function syncURL() { return config.proxyBase + '/sync'; }

  function postSync(ids) {
    return fetch(syncURL(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ ids: ids })
    }).then(function (r) {
      if (!r.ok) throw new Error('wishlist sync ' + r.status);
      return r.json();
    });
  }

  function beaconSync(ids) {
    if (!navigator.sendBeacon) return false;
    try {
      var blob = new Blob([JSON.stringify({ ids: ids })], { type: 'application/json' });
      return navigator.sendBeacon(syncURL(), blob);
    } catch (e) { return false; }
  }

  // --- flush scheduling -------------------------------------------------

  function scheduleFlush() {
    if (!config.loggedIn) return;
    setPending(true);
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flushNow, DEBOUNCE_MS);
  }

  function flushNow() {
    if (!config.loggedIn) return Promise.resolve();
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }

    var ids = Array.from(saved);
    return postSync(ids)
      .then(function (res) {
        if (res && Array.isArray(res.saved)) applyServerState(res.saved);
        setPending(false);
      })
      .catch(function () { /* keep pending flag set; retry on next page load */ });
  }

  function flushOnHide() {
    if (!config.loggedIn || !hasPending()) return;
    var ids = Array.from(saved);
    if (beaconSync(ids)) setPending(false);
  }

  // --- UI sync ----------------------------------------------------------

  function isSaved(id) { return saved.has(String(id)); }

  function syncButton(btn) {
    var id = btn.dataset.productId;
    if (!id) return;
    var active = isSaved(id);
    btn.classList.toggle('is-saved', active);
    var title = btn.dataset.productTitle || '';
    btn.setAttribute('aria-label', (active ? 'Remove from wishlist' : 'Add to wishlist') + (title ? ': ' + title : ''));
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(syncButton);
    syncHeaderIcon();
  }

  function syncHeaderIcon() {
    var count = saved.size;
    document.querySelectorAll('[data-wishlist-header-link]').forEach(function (link) {
      link.classList.toggle('has-items', count > 0);
      var bubble = link.querySelector('[data-wishlist-count]');
      if (bubble) bubble.textContent = count > 0 ? (count > 99 ? '99+' : String(count)) : '';
      var label = link.querySelector('[data-wishlist-count-label]');
      if (label) label.textContent = count > 0 ? 'Wishlist (' + count + ' item' + (count === 1 ? '' : 's') + ')' : 'Wishlist';
    });
  }

  function dispatch(id, isSavedNow) {
    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: { productId: id, saved: isSavedNow }
    }));
  }

  function applyServerState(ids) {
    saved = new Set((ids || []).map(String));
    writeLocal();
    syncAllButtons();
  }

  // --- public actions ---------------------------------------------------

  function toggle(id) {
    id = String(id);
    var willAdd = !saved.has(id);

    if (willAdd) saved.add(id); else saved.delete(id);
    writeLocal();
    document.querySelectorAll('[data-wishlist-btn][data-product-id="' + id + '"]').forEach(function (b) {
      b.classList.toggle('is-saved', willAdd);
    });
    syncHeaderIcon();
    dispatch(id, willAdd);

    scheduleFlush();
  }

  // --- init -------------------------------------------------------------

  var localIds = readLocal();

  if (config.loggedIn) {
    var initial = (config.initialIds || []).map(String);
    var union = new Set(initial);
    localIds.forEach(function (id) { union.add(id); });
    saved = union;
    writeLocal();

    var serverSet = new Set(initial);
    var differs = saved.size !== serverSet.size;
    if (!differs) {
      for (var id of saved) { if (!serverSet.has(id)) { differs = true; break; } }
    }
    if (differs || hasPending()) scheduleFlush();
  } else {
    saved = new Set(localIds);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    var id = btn.dataset.productId;
    if (!id) return;
    toggle(id);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAllButtons);
  } else {
    syncAllButtons();
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushOnHide();
  });
  window.addEventListener('pagehide', flushOnHide);

  window.WishlistStore = {
    isSaved: isSaved,
    toggle: toggle,
    getIds: function () { return Array.from(saved); },
    flush: flushNow,
    loggedIn: config.loggedIn
  };
})();
